// ── Per-channel consent read-derivation (dashboard shared mirror) ────────────
//
// SOURCE OF TRUTH is the Python `derive_channel_consent_enums` in
// contacts-db-backend/functions/contact_key_utils.py (and its TS twin in
// backends/marketing/src/domain/marketing/consent.ts). This module is the
// single dashboard-side mirror of that D23 read-mapping. It exists because the
// dashboard reads contact rows from OpenSearch `/contacts`, which during the
// skew window may carry a row indexed BEFORE the consent updater deployed (no
// enum fields) or, for the Edit-Contact drawer, the RAW DDB item from
// GET /validate/contact (also enum-less for legacy rows). Both surfaces MUST
// resolve consent identically — the contacts table column and the drawer toggle
// disagreeing is INV-21 (a PF-suppressed contact reading "Subscribed" anywhere
// is the exact failure the initiative exists to prevent).
//
// Keep in lockstep with the Python source of truth. Deletable once every
// consumer receives derived enums (BE derive at /validate/contact + OpenSearch
// resync) — until then this is the one dashboard fallback, imported by the
// column cell AND the drawer edit-seed (never re-rolled inline).
//
// UI label for UNKNOWN (2026-07-24, display-only — matches NEVER_SUBSCRIBED's
// label for consistency; decisions.md "unknown-sms-consent-label" entry).
// Does NOT change the stored enum, sendability, or consentAudit provenance —
// UNKNOWN already behaves identically to NEVER_SUBSCRIBED for sending (neither
// is ever sendable); only the admin-facing word changed. Never render "unknown".

export type ChannelConsentStatus =
  | 'SUBSCRIBED'
  | 'UNSUBSCRIBED'
  | 'NEVER_SUBSCRIBED'
  | 'UNKNOWN';

export const CONSENT_ENUM_VALUES: ReadonlySet<string> = new Set([
  'SUBSCRIBED',
  'UNSUBSCRIBED',
  'NEVER_SUBSCRIBED',
  'UNKNOWN',
]);

export const CONSENT_STATUS_LABEL: Record<ChannelConsentStatus, string> = {
  SUBSCRIBED: 'Subscribed',
  UNSUBSCRIBED: 'Unsubscribed',
  NEVER_SUBSCRIBED: 'Never subscribed',
  UNKNOWN: 'Never subscribed',
};

// Badge variant per status. success = sendable; danger = explicit decline;
// neutral = never-asked / no-record (not an error, just not sendable).
export const CONSENT_STATUS_VARIANT: Record<ChannelConsentStatus, string> = {
  SUBSCRIBED: 'success',
  UNSUBSCRIBED: 'danger',
  NEVER_SUBSCRIBED: 'neutral',
  UNKNOWN: 'neutral',
};

// The not-suppressed sentinels — mirrors Python `_PF_NOT_SUPPRESSED = {"no",""}`.
const PF_NOT_SUPPRESSED: ReadonlySet<string> = new Set(['no', '']);
// SMS-scoped suppression sentinel (a texted STOP, INV-17). Mirrors Python
// `PF_SCOPE_SMS`. Floors ONLY sms; email + master derive normally.
export const PF_SCOPE_SMS = 'sms';
// Narrow per-object opt-out scopes (PE-1845) — a single-campaign / single-trigger
// unsubscribe, NOT a global suppression. Floor NEITHER channel; PF enforces the
// per-object block. Mirrors Python `_PF_PARTIAL_SCOPES`.
const PF_PARTIAL_SCOPES: ReadonlySet<string> = new Set(['campaign', 'trigger']);

type ConsentContact = {
  emailConsent?: unknown;
  smsConsent?: unknown;
  communicationConsent?: unknown;
  pocketflow_unsubscribed?: unknown;
};

const pocketflowScope = (contact: ConsentContact | null | undefined): string => {
  const raw = contact?.pocketflow_unsubscribed;
  if (raw === undefined || raw === null) return '';
  return String(raw).trim().toLowerCase();
};

// True when the contact carries an active FULL PocketFlows suppression — a hard
// floor over BOTH channels. Mirrors Python `_pocketflow_suppressed`: 'no'/''/
// absent are not suppressed, the 'sms' sentinel is a channel-scoped opt-out
// (handled separately), the 'campaign'/'trigger' partial scopes floor NEITHER
// channel (PE-1845), and any OTHER scope is a full floor.
export const isPocketflowSuppressed = (
  contact: ConsentContact | null | undefined,
): boolean => {
  const scope = pocketflowScope(contact);
  return (
    !PF_NOT_SUPPRESSED.has(scope) &&
    scope !== PF_SCOPE_SMS &&
    !PF_PARTIAL_SCOPES.has(scope)
  );
};

// True when the contact carries the SMS-scoped opt-out ('sms').
export const isPocketflowSmsScoped = (
  contact: ConsentContact | null | undefined,
): boolean => pocketflowScope(contact) === PF_SCOPE_SMS;

// Resolve the enum for one channel. Resolution order MIRRORS the Python
// source-of-truth `derive_channel_consent_enums` EXACTLY:
//   1. FULL PF suppression → UNSUBSCRIBED for BOTH channels (hard floor,
//      wins over any stored enum). Fail-closed.
//   1b. SMS-scoped suppression ('sms') → floors ONLY sms; email derives
//      normally. A texted STOP must never present email as unsubscribed.
//   2. A stored, valid enum wins.
//   3. Else derive from the legacy communicationConsent boolean (D23): email
//      SUBSCRIBED (opt-out regime); sms SUBSCRIBED iff legacy master true, else
//      UNKNOWN (legacy-ambiguous).
export const resolveChannelConsent = (
  contact: ConsentContact | null | undefined,
  channel: 'email' | 'sms',
): ChannelConsentStatus => {
  if (isPocketflowSuppressed(contact)) return 'UNSUBSCRIBED';
  if (channel === 'sms' && isPocketflowSmsScoped(contact)) return 'UNSUBSCRIBED';
  const stored = contact?.[channel === 'email' ? 'emailConsent' : 'smsConsent'];
  if (typeof stored === 'string' && CONSENT_ENUM_VALUES.has(stored)) {
    return stored as ChannelConsentStatus;
  }
  if (channel === 'email') return 'SUBSCRIBED';
  const legacyTrue = contact?.communicationConsent === true;
  return legacyTrue ? 'SUBSCRIBED' : 'UNKNOWN';
};
